import React, { useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Loading } from "@/components/atoms";
import Shell from "@/components/Shell";
import Auth from "@/pages/Auth";
import Offers from "@/pages/Offers";
import Gallery from "@/pages/Gallery";
import Artists from "@/pages/Artists";
import Reviews from "@/pages/Reviews";
import Chat from "@/pages/Chat";
import Bookings from "@/pages/Bookings";
import Messages from "@/pages/Messages";
import Profile from "@/pages/Profile";
import Membership from "@/pages/Membership";
import Admin from "@/pages/Admin";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import api from "@/lib/api";
import "@/App.css";

function AuthCallback() {
  const navigate = useNavigate();
  const { setUser, loadBranding } = useAuth();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const hash = window.location.hash;
    const sid = new URLSearchParams(hash.replace("#", "")).get("session_id");
    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", {}, { headers: { "X-Session-ID": sid } });
        setUser(data);
        loadBranding();
      } catch (e) {
        /* fall through to login */
      } finally {
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/", { replace: true });
      }
    })();
  }, [navigate, setUser, loadBranding]);
  return <Loading label="Signing you in" />;
}

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loading label="Loading the club" />;
  if (user === false) return <Navigate to="/auth" replace />;
  return <Shell>{children}</Shell>;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/auth" element={<Auth />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/" element={<Protected><Offers /></Protected>} />
      <Route path="/gallery" element={<Protected><Gallery section="gallery" title="Gallery" eyebrow="Healed & Fresh" /></Protected>} />
      <Route path="/flash" element={<Protected><Gallery section="flash" allowBook title="Flash" eyebrow="Book A Design" /></Protected>} />
      <Route path="/artists" element={<Protected><Artists /></Protected>} />
      <Route path="/reviews" element={<Protected><Reviews /></Protected>} />
      <Route path="/chat" element={<Protected><Chat /></Protected>} />
      <Route path="/bookings" element={<Protected><Bookings /></Protected>} />
      <Route path="/messages" element={<Protected><Messages /></Protected>} />
      <Route path="/profile" element={<Protected><Profile /></Protected>} />
      <Route path="/membership" element={<Protected><Membership /></Protected>} />
      <Route path="/admin" element={<Protected><Admin /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-center" richColors />
        <AppRouter />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
